/* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useState, useEffect, type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import ReactFocusLock from 'react-focus-lock';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useAdminPageSidebar } from '../../controllers/sidebar/index.tsx';
import type { AdminPageSidebar } from '../../controllers/sidebar/types.tsx';
import { DURATION, LoadingBar } from './loading-bar';

const Sidebar = ({
	sidebarContent: { header, content, footer, loadingBar = true },
}: {
	sidebarContent: AdminPageSidebar;
}) => {
	const [loading, setLoading] = useState<boolean>(loadingBar);
	useEffect(() => {
		setLoading(loadingBar);
		if (loadingBar) {
			setTimeout(() => setLoading(false), DURATION);
		}
	}, [header, content, footer, loadingBar]);
	return (
		<Box xcss={SidebarContainer}>
			<Box xcss={HeaderContainer}>{header}</Box>
			{loading ? (
				<LoadingBar />
			) : (
				<>
					<Box xcss={ContentContainer}>{content}</Box>
					{footer ? <Box xcss={FooterContainer}>{footer}</Box> : null}
				</>
			)}
		</Box>
	);
};

const AdminPageWithSidebar = ({ children }: { children: ReactNode }) => {
	const {
		state: { sidebarContent },
	} = useAdminPageSidebar();
	return (
		<Flex xcss={AdminPageContainer}>
			{children}
			<ExitingPersistence>
				{sidebarContent ? (
					<SlideIn enterFrom="right">
						{({ className, ref }) => (
							<ReactFocusLock
								autoFocus
								returnFocus
								focusOptions={{ preventScroll: true }}
								ref={ref}
								as="aside"
								css={AsideStyles}
								className={className}
							>
								<Sidebar sidebarContent={sidebarContent} />
							</ReactFocusLock>
						)}
					</SlideIn>
				) : null}
			</ExitingPersistence>
		</Flex>
	);
};

export default AdminPageWithSidebar;

const AdminPageContainer = xcss({
	height: '100%',
	overflowX: 'hidden',
});

const AsideStyles = css({
	position: 'sticky',
	top: '0px',
});

const SidebarContainer = xcss({
	height: '100%',
	minWidth: '420px',
	display: 'flex',
	flexDirection: 'column',
	borderLeftColor: 'color.border',
	borderLeftStyle: 'solid',
	borderLeftWidth: '1px',
});

const HeaderContainer = xcss({
	display: 'flex',
	flexDirection: 'column',
	paddingTop: 'space.300',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.200',
	borderBottomColor: 'color.border',
	borderBottomStyle: 'solid',
	borderBottomWidth: '1px',
});

const ContentContainer = xcss({
	display: 'flex',
	flexDirection: 'column',
	flex: '1',
	paddingTop: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.200',
	overflowY: 'hidden',
	':hover': {
		overflowY: 'auto',
	},
	':focus-within': {
		overflowY: 'auto',
	},
});

const FooterContainer = xcss({
	justifySelf: 'end',
	display: 'flex',
	flexDirection: 'column',
	borderTopColor: 'color.border',
	borderTopStyle: 'solid',
	borderTopWidth: '1px',
	paddingTop: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	paddingBottom: 'space.200',
});
